import React, { useState, useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import { ticketGet } from '../../services/apiClient';
import { registrationService } from '../../api/registrationService';
import { extractErrorMessage } from '../../utils/errorHandler';
import { excludeSuperAdminFromReportRows, isSuperAdminRole } from '../../utils/reportFilters';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button, buttonVariants } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/Select';
import { cn } from '../../utils/cn';
import {
  AlertTriangle,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  BarChart3,
  Download,
  ChevronDown,
  FileText,
  Search,
  Shield
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const CHART_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];

const roleSelectItemClass =
  'hover:!bg-[#E0F8F3] focus:!bg-[#E0F8F3] !text-[#000000] dark:!text-gray-100 [&.font-medium]:!bg-[#E0F8F3] [&.font-medium]:!text-[#000000]';

const parseListPayload = (response) => {
  if (response?.success && Array.isArray(response?.data)) {
    return response.data;
  }
  if (response?.data?.success && Array.isArray(response?.data?.data)) {
    return response.data.data;
  }
  if (Array.isArray(response?.data)) {
    return response.data;
  }
  if (Array.isArray(response)) {
    return response;
  }
  return [];
};

const parsePagePayload = (response) => {
  if (response?.success && response?.data) {
    return response.data;
  }
  if (response?.data?.success && response?.data?.data) {
    return response.data.data;
  }
  if (response?.data) {
    return response.data;
  }
  return null;
};

const AgentWiseSLABreachReport = () => {
  const [tableRows, setTableRows] = useState([]);
  const [chartSourceData, setChartSourceData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [chartLoading, setChartLoading] = useState(false);
  const [error, setError] = useState('');

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState('all');

  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(10);
  const [paginationData, setPaginationData] = useState(null);

  const [exporting, setExporting] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const loadRoles = async () => {
      setLoadingRoles(true);
      try {
        const rolesResp = await registrationService.getRoles();
        if (!cancelled) {
          const rawRoles = Array.isArray(rolesResp?.data) ? rolesResp.data : [];
          setRoles(
            rawRoles.filter((role) => {
              if (role.roleId == null) return false;
              const code = String(role.roleCode || '').toUpperCase();
              if (role.isRequesterRole === true) return false;
              if (code === 'ADMIN') return false;
              if (isSuperAdminRole(code)) return false;
              return true;
            })
          );
        }
      } catch (err) {
        console.warn('Failed to load roles:', err);
        if (!cancelled) {
          setRoles([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingRoles(false);
        }
      }
    };
    loadRoles();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setCurrentPage(0);
  }, [debouncedSearchTerm, selectedRoleId]);

  useEffect(() => {
    loadSlaComplianceStatistics();
  }, [debouncedSearchTerm, selectedRoleId]);

  useEffect(() => {
    loadSlaComplianceTable();
  }, [currentPage, pageSize, debouncedSearchTerm, selectedRoleId]);

  const buildFilterParams = () => {
    const params = {};
    const trimmedSearch = debouncedSearchTerm.trim();
    if (trimmedSearch) params.search = trimmedSearch;
    if (selectedRoleId !== 'all') {
      const roleId = parseInt(selectedRoleId, 10);
      if (!Number.isNaN(roleId)) {
        params.roleId = roleId;
      }
    }
    return params;
  };

  const normalizeSlaRows = (rows) =>
    excludeSuperAdminFromReportRows(
      (Array.isArray(rows) ? rows : []).map((item) => ({
        ...item,
        assigneeName: item.assigneeName || item.key || 'Unknown Agent',
        assigneeId: item.assigneeId || item.userId || item.id
      }))
    );

  const loadSlaComplianceStatistics = async () => {
    try {
      setChartLoading(true);
      const response = await ticketGet(
        '/gateway/api/ticket/analytics/sla-compliance/statistics',
        buildFilterParams()
      );
      setChartSourceData(normalizeSlaRows(parseListPayload(response)));
    } catch (err) {
      console.warn('SLA compliance statistics load failed:', err);
      setChartSourceData([]);
    } finally {
      setChartLoading(false);
    }
  };

  const loadSlaComplianceTable = async () => {
    try {
      setLoading(true);
      setError('');

      const params = {
        ...buildFilterParams(),
        page: currentPage,
        size: pageSize
      };

      const response = await ticketGet('/gateway/api/ticket/analytics/sla-compliance', params);
      const pagePayload = parsePagePayload(response);

      const content = Array.isArray(pagePayload?.content)
        ? pagePayload.content
        : (Array.isArray(pagePayload) ? pagePayload : []);

      const rows = normalizeSlaRows(content);
      setTableRows(rows);
      setPaginationData({
        totalElements: pagePayload?.totalElements ?? 0,
        totalPages: pagePayload?.totalPages ?? 0,
        pageNumber: pagePayload?.pageNumber ?? currentPage,
        pageSize: pagePayload?.pageSize ?? pageSize,
        first: pagePayload?.first ?? currentPage === 0,
        last: pagePayload?.last ?? true
      });
    } catch (err) {
      console.error('Error loading SLA breach report:', err);
      setError(extractErrorMessage(err, 'Failed to load SLA breach report'));
      toast.error(extractErrorMessage(err, 'Failed to load SLA breach report'));
      setTableRows([]);
      setPaginationData(null);
    } finally {
      setLoading(false);
    }
  };

  const refreshAll = () => {
    loadSlaComplianceStatistics();
    loadSlaComplianceTable();
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 0 && (paginationData ? newPage < paginationData.totalPages : true)) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleRoleChange = (value) => {
    setSelectedRoleId(value);
    setCurrentPage(0);
  };

  const handleRoleTriggerFocus = (event) => {
    const trigger = event.currentTarget;
    trigger.style.setProperty('--tw-ring-color', 'transparent');
    trigger.style.setProperty('--tw-ring-shadow', '0 0 #0000');
    trigger.style.setProperty('--tw-ring-offset-shadow', '0 0 #0000');
    trigger.style.boxShadow = 'none';
    trigger.style.outline = 'none';
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setSelectedRoleId('all');
    setCurrentPage(0);
  };

  const formatPercentage = (value) => {
    if (value === null || value === undefined) return '0.00';
    return typeof value === 'number' ? value.toFixed(2) : parseFloat(value).toFixed(2);
  };

  const hasActiveFilters = searchTerm.trim() !== '' || selectedRoleId !== 'all';

  const histogramChartData = useMemo(
    () =>
      chartSourceData
        .map((item) => ({
          name: (item.assigneeName || item.key || 'Unknown').substring(0, 20),
          count: Number(item.totalCount) || 0
        }))
        .filter((d) => d.count > 0),
    [chartSourceData]
  );

  const barChartData = useMemo(
    () =>
      chartSourceData.map((item) => ({
        name: (item.assigneeName || item.key || 'Unknown').substring(0, 12),
        total: Number(item.totalCount) || 0,
        slaMet: Number(item.slaMetCount) || 0,
        slaBreached: Number(item.slaBreachedCount) || 0,
        compliancePct: Number(item.slaCompliancePercentage) || 0
      })),
    [chartSourceData]
  );

  const escapeCsvCell = (val) => {
    if (val == null || val === '') return '';
    const s = String(val);
    if (/[,"\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };

  const handleExportCsv = async () => {
    setExportMenuOpen(false);
    setExporting(true);
    try {
      const response = await ticketGet('/gateway/api/ticket/analytics/report', {
        type: 'SLA_COMPLIANCE',
        ...buildFilterParams()
      });
      const exportData = normalizeSlaRows(parseListPayload(response));
      if (exportData.length === 0) {
        toast.error('No data to export');
        return;
      }
      const headers = ['S.No', 'Agent Name', 'Total Tickets', 'SLA Breached', 'SLA Met', 'SLA Compliance %'];
      const rows = exportData.map((r, i) => [
        i + 1,
        r.assigneeName || r.key || 'Unknown Agent',
        r.totalCount || 0,
        r.slaBreachedCount || 0,
        r.slaMetCount || 0,
        `${formatPercentage(r.slaCompliancePercentage)}%`
      ].map(escapeCsvCell));
      const csv = [headers.map(escapeCsvCell).join(','), ...rows.map((r) => r.join(','))].join('\r\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `agent-wise-sla-breach-report-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Report exported as CSV');
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = async () => {
    setExportMenuOpen(false);
    setExporting(true);
    try {
      const response = await ticketGet('/gateway/api/ticket/analytics/report', {
        type: 'SLA_COMPLIANCE',
        ...buildFilterParams()
      });
      const exportData = normalizeSlaRows(parseListPayload(response));
      if (exportData.length === 0) {
        toast.error('No data to export');
        return;
      }
      const doc = new jsPDF({ orientation: 'landscape' });
      doc.setFontSize(14);
      doc.text('Agent wise SLA Breach Report', 14, 15);
      doc.setFontSize(10);
      const head = [['S.No', 'Agent Name', 'Total Tickets', 'SLA Breached', 'SLA Met', 'SLA Compliance %']];
      const body = exportData.map((r, i) => [
        i + 1,
        (r.assigneeName || r.key || 'Unknown Agent').substring(0, 30),
        r.totalCount || 0,
        r.slaBreachedCount || 0,
        r.slaMetCount || 0,
        `${formatPercentage(r.slaCompliancePercentage)}%`
      ]);
      autoTable(doc, { startY: 22, head, body, styles: { fontSize: 9 } });
      doc.save(`agent-wise-sla-breach-report-${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success('Report exported as PDF');
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Agent wise SLA Breach Report</h1>
          <p className="text-sm text-muted-foreground mt-1">
            View SLA compliance statistics for each agent
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <div className="relative w-[220px]">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search agents..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(0);
              }}
              className="h-9 pl-10"
            />
          </div>
          <Select
            value={selectedRoleId}
            onValueChange={handleRoleChange}
            disabled={loadingRoles}
          >
            <SelectTrigger
              onFocus={handleRoleTriggerFocus}
              className={cn(
                '!h-9 !min-w-[200px] !w-[220px] !max-w-[260px] !border-primary gap-2 px-3 text-sm font-medium rounded-xl text-[#000000]',
                buttonVariants.outline,
                'shadow-none focus:shadow-none focus-visible:shadow-none',
                'focus:outline-none focus-visible:outline-none',
                '!focus:ring-0 !focus:ring-offset-0 !focus:ring-transparent focus:[--tw-ring-color:transparent]',
                'focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:ring-transparent focus-visible:[--tw-ring-color:transparent]',
                '!focus:border-primary focus-visible:border-primary active:border-primary'
              )}
            >
              <Shield className="h-4 w-4 shrink-0" />
              <SelectValue placeholder="All Roles" />
            </SelectTrigger>
            <SelectContent className="rounded-xl min-w-[220px]">
              <SelectItem value="all" className={roleSelectItemClass}>All Roles</SelectItem>
              {roles.map((role) => (
                <SelectItem
                  key={role.roleId}
                  value={String(role.roleId)}
                  className={roleSelectItemClass}
                >
                  {role.label || role.roleCode}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {hasActiveFilters && (
            <Button variant="outline" size="sm" onClick={handleClearFilters} className="h-9">
              Clear
            </Button>
          )}
          <div className="relative">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setExportMenuOpen((v) => !v)}
              disabled={loading || chartLoading || exporting}
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              {exporting ? 'Exporting…' : 'Export'}
              <ChevronDown className="h-4 w-4 ml-1" />
            </Button>
            {exportMenuOpen && (
              <div className="absolute right-0 top-full mt-1 py-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-10 min-w-[140px]">
                <button type="button" className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2" onClick={handleExportCsv}>
                  <FileText className="h-4 w-4" /> Export CSV
                </button>
                <button type="button" className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2" onClick={handleExportPdf}>
                  <FileText className="h-4 w-4" /> Export PDF
                </button>
              </div>
            )}
          </div>
          <Button
            onClick={refreshAll}
            disabled={loading || chartLoading}
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${loading || chartLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {!loading && !chartLoading && chartSourceData.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <BarChart3 className="h-5 w-5" />
                Tickets by agent (histogram)
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Total tickets per agent
              </p>
            </CardHeader>
            <CardContent>
              {histogramChartData.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">No data to display.</p>
              ) : (
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart
                    data={histogramChartData}
                    margin={{ top: 8, right: 8, left: 8, bottom: 24 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="name" className="text-xs" tick={{ fontSize: 10 }} />
                    <YAxis allowDecimals={false} className="text-xs" />
                    <Tooltip
                      formatter={(value) => [value, 'Tickets']}
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                      labelFormatter={(name) => name}
                    />
                    <Bar dataKey="count" fill={CHART_COLORS[0]} name="Tickets" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <BarChart3 className="h-5 w-5" />
                SLA Met vs Breached by agent
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Green = SLA Met, Red = SLA Breached
              </p>
            </CardHeader>
            <CardContent>
              {barChartData.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">No data to display.</p>
              ) : (
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart
                    data={barChartData}
                    margin={{ top: 8, right: 8, left: 8, bottom: 24 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="name" className="text-xs" tick={{ fontSize: 10 }} />
                    <YAxis allowDecimals={false} className="text-xs" />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                      formatter={(value) => [value]}
                    />
                    <Legend wrapperStyle={{ fontSize: '11px' }} />
                    <Bar dataKey="slaMet" stackId="sla" fill="#10B981" name="SLA Met" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="slaBreached" stackId="sla" fill="#EF4444" name="SLA Breached" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            SLA Compliance Statistics
            {paginationData && (
              <span className="text-sm font-normal text-muted-foreground ml-2">
                ({paginationData.totalElements} total)
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-800 text-sm">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-muted-foreground">Loading SLA breach report...</span>
            </div>
          ) : tableRows.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <AlertTriangle className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No SLA data found</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b border-border bg-muted/50">
                      <th className="text-left p-3 text-sm font-semibold text-foreground">Agent Name</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground">Total Tickets</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground">SLA Breached</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground">SLA Met</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground">SLA Compliance %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableRows.map((slaDataItem, index) => (
                      <tr
                        key={slaDataItem.assigneeId || slaDataItem.userId || slaDataItem.id || index}
                        className="border-b border-border hover:bg-muted/30 transition-colors"
                      >
                        <td className="p-3 text-sm text-foreground font-medium">
                          {slaDataItem.assigneeName || slaDataItem.key || 'Unknown Agent'}
                        </td>
                        <td className="p-3 text-sm text-foreground">
                          {slaDataItem.totalCount || 0}
                        </td>
                        <td className="p-3 text-sm text-foreground">
                          <span className="text-red-600 font-medium">
                            {slaDataItem.slaBreachedCount || 0}
                          </span>
                        </td>
                        <td className="p-3 text-sm text-foreground">
                          <span className="text-green-600 font-medium">
                            {slaDataItem.slaMetCount || 0}
                          </span>
                        </td>
                        <td className="p-3 text-sm text-foreground">
                          <span className={`font-medium ${
                            (slaDataItem.slaCompliancePercentage || 0) >= 90 ? 'text-green-600' :
                            (slaDataItem.slaCompliancePercentage || 0) >= 70 ? 'text-yellow-600' :
                            'text-red-600'
                          }`}>
                            {formatPercentage(slaDataItem.slaCompliancePercentage)}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {paginationData && paginationData.totalElements > 0 && (
                <div className="flex flex-col sm:flex-row items-center justify-between mt-6 pt-4 border-t border-border gap-4">
                  <div className="text-sm text-muted-foreground">
                    Showing {currentPage * pageSize + 1} to{' '}
                    {Math.min((currentPage + 1) * pageSize, paginationData.totalElements)} of{' '}
                    {paginationData.totalElements} agents
                  </div>
                  <div className="flex items-center gap-2">
                    {paginationData.totalPages > 1 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(0)}
                        disabled={currentPage === 0 || loading}
                        className="hidden sm:flex"
                      >
                        First
                      </Button>
                    )}

                    {paginationData.totalPages > 1 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 0 || loading}
                      >
                        <ChevronLeft className="h-4 w-4" />
                        Previous
                      </Button>
                    )}

                    {paginationData.totalPages > 0 && (
                      <div className="flex items-center gap-1">
                        {(() => {
                          const pages = [];
                          const totalPages = paginationData.totalPages;
                          const current = currentPage + 1;

                          if (totalPages <= 7) {
                            for (let i = 1; i <= totalPages; i++) {
                              pages.push(
                                <Button
                                  key={i}
                                  variant={i === current ? 'default' : 'outline'}
                                  size="sm"
                                  onClick={() => handlePageChange(i - 1)}
                                  disabled={loading || totalPages === 1}
                                  className="min-w-[2.5rem]"
                                >
                                  {i}
                                </Button>
                              );
                            }
                          } else {
                            pages.push(
                              <Button
                                key={1}
                                variant={current === 1 ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => handlePageChange(0)}
                                disabled={loading}
                                className="min-w-[2.5rem]"
                              >
                                1
                              </Button>
                            );

                            if (current > 3) {
                              pages.push(
                                <span key="ellipsis-start" className="px-2 text-muted-foreground">
                                  ...
                                </span>
                              );
                            }

                            const start = Math.max(2, current - 1);
                            const end = Math.min(totalPages - 1, current + 1);

                            for (let i = start; i <= end; i++) {
                              if (i !== 1 && i !== totalPages) {
                                pages.push(
                                  <Button
                                    key={i}
                                    variant={i === current ? 'default' : 'outline'}
                                    size="sm"
                                    onClick={() => handlePageChange(i - 1)}
                                    disabled={loading}
                                    className="min-w-[2.5rem]"
                                  >
                                    {i}
                                  </Button>
                                );
                              }
                            }

                            if (current < totalPages - 2) {
                              pages.push(
                                <span key="ellipsis-end" className="px-2 text-muted-foreground">
                                  ...
                                </span>
                              );
                            }

                            pages.push(
                              <Button
                                key={totalPages}
                                variant={current === totalPages ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => handlePageChange(totalPages - 1)}
                                disabled={loading}
                                className="min-w-[2.5rem]"
                              >
                                {totalPages}
                              </Button>
                            );
                          }

                          return pages;
                        })()}
                      </div>
                    )}

                    {paginationData.totalPages > 1 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage >= paginationData.totalPages - 1 || loading}
                      >
                        Next
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    )}

                    {paginationData.totalPages > 1 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(paginationData.totalPages - 1)}
                        disabled={currentPage >= paginationData.totalPages - 1 || loading}
                        className="hidden sm:flex"
                      >
                        Last
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AgentWiseSLABreachReport;
